import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { Autocomplete, IconButton, TextField } from '@totalsoft/rocket-ui';
import CancelIcon from '@mui/icons-material/Cancel';
import debounce from 'lodash.debounce';

const OfferPartnerAddress = ({
  t,
  onPropertyChange,
  address,
  counties,
  countries,
  disabled,
  checkMandatoryFields,
  validationInfo,
  loadCities,
  index,
  onDeleteAddress
}) => {
  const internalLoadCities = input => {
    if (!input) {
      return Promise.resolve([]);
    }

    return loadCities(input);
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            id={'offerPartnerAddressCard_' + index + '_country'}
            fullWidth
            required={checkMandatoryFields}
            value={address.country}
            error={validationInfo && validationInfo.country}
            helperText={validationInfo && validationInfo.country}
            onChange={onPropertyChange('country')}
            label={t('OfferEdit.Client.Country')}
            valueKey='id'
            labelKey='name'
            name='country'
            options={countries}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            id={'offerPartnerAddressCard_' + index + '_county'}
            fullWidth
            value={address.county}
            error={validationInfo && validationInfo.county}
            helperText={validationInfo && validationInfo.county}
            onChange={onPropertyChange('county')}
            label={t('OfferEdit.Client.County')}
            valueKey='id'
            labelKey='name'
            name='county'
            options={counties}
            required={checkMandatoryFields}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Autocomplete
            id={'offerPartnerAddressCard_' + index + '_city'}
            fullWidth
            cache={false}
            value={address.city}
            error={validationInfo && validationInfo.city}
            helperText={validationInfo && validationInfo.city}
            onChange={onPropertyChange('city')}
            label={t('OfferEdit.Client.City')}
            valueKey='id'
            labelKey='name'
            name='city'
            loadOptions={debounce(internalLoadCities, 500, { leading: true })}
            required={checkMandatoryFields}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={'offerPartnerAddressCard_' + index + '_street'}
            fullWidth
            value={address.street || ''}
            error={validationInfo && validationInfo.street}
            helperText={validationInfo && validationInfo.street}
            name='street'
            label={t('OfferEdit.Client.Street')}
            onChange={onPropertyChange('street')}
            required={checkMandatoryFields}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={'offerPartnerAddressCard_' + index + '_number'}
            fullWidth
            value={address.number || ''}
            name='number'
            label={t('OfferEdit.Client.Number')}
            onChange={onPropertyChange('number')}
            error={validationInfo && validationInfo.number}
            helperText={validationInfo && validationInfo.number}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={'offerPartnerAddressCard_' + index + '_building'}
            fullWidth
            value={address.building || ''}
            name='building'
            label={t('OfferEdit.Client.Building')}
            onChange={onPropertyChange('building')}
            error={validationInfo && validationInfo.building}
            helperText={validationInfo && validationInfo.building}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={'offerPartnerAddressCard_' + index + '_stair'}
            fullWidth
            value={address.stair || ''}
            name='stair'
            label={t('OfferEdit.Client.Stair')}
            onChange={onPropertyChange('stair')}
            error={validationInfo && validationInfo.stair}
            helperText={validationInfo && validationInfo.stair}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={'offerPartnerAddressCard_' + index + '_flatNumber'}
            fullWidth
            value={address.flatNumber || ''}
            name='flatNumber'
            label={t('OfferEdit.Client.FlatNumber')}
            onChange={onPropertyChange('flatNumber')}
            error={validationInfo && validationInfo.flatNumber}
            helperText={validationInfo && validationInfo.flatNumber}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={'offerPartnerAddressCard_' + index + '_floor'}
            fullWidth
            value={address.floor || ''}
            name='floor'
            label={t('OfferEdit.Client.Floor')}
            onChange={onPropertyChange('floor')}
            error={validationInfo && validationInfo.floor}
            helperText={validationInfo && validationInfo.floor}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={'offerPartnerAddressCard_' + index + '_zipCode'}
            fullWidth
            value={address.zipCode || ''}
            name='zipCode'
            label={t('OfferEdit.Client.ZipCode')}
            onChange={onPropertyChange('zipCode')}
            error={validationInfo && validationInfo.zipCode}
            helperText={validationInfo && validationInfo.zipCode}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            id={'offerPartnerAddressCard_' + index + '_neighborhood'}
            fullWidth
            value={address.neighborhood || ''}
            name='neighborhood'
            label={t('OfferEdit.Client.Neighborhood')}
            onChange={onPropertyChange('neighborhood')}
            error={validationInfo && validationInfo.neighborhood}
            helperText={validationInfo && validationInfo.neighborhood}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <TextField
            id={'offerPartnerAddressCard_' + index + '_full'}
            fullWidth
            value={address.syntheticName || ''}
            name='fullAddress'
            label={t('OfferEdit.Client.FullAddress')}
            disabled
          />
        </Grid>
        {!address.isDefault && (
          <Grid item container justifyContent='end'>
            <IconButton
              id={'offerPartnerAddressCard_' + index + '_deleteBtn'}
              color='primary'
              variant='text'
              size='small'
              onClick={() => onDeleteAddress(index)}
            >
              <CancelIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>
    </>
  );
};

OfferPartnerAddress.propTypes = {
  t: PropTypes.func.isRequired,
  address: PropTypes.object.isRequired,
  loadCities: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  counties: PropTypes.array.isRequired,
  countries: PropTypes.array.isRequired,
  config: PropTypes.object.isRequired,
  disabled: PropTypes.bool.isRequired,
  checkMandatoryFields: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  index: PropTypes.number,
  onDeleteAddress: PropTypes.func
};

export default compose(withTranslation())(OfferPartnerAddress);
